import { CanAccess } from "@refinedev/core";
import { useRef } from "react";
import { Authenticated, Refine } from "@refinedev/core";
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
  CatchAllNavigate,
} from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { useAtomValue } from "jotai";
import { roleAtom } from "@auth/firebase/state";
import { resources } from "resources";
import { authProvider } from "auth/firebase";
import { accessControlProvider } from "access/accessControlProvider";
import hasuraDataProvider from "data/hasura";
import gapiDataProviderFunc from "data/gapi";
import { useGoogleApi } from "hooks/google/useGoogleApi";
import { RefreshTokens } from "@components/utils/RefreshTokens";
import { AppTemplate } from "@components/templates/app";
import { CustomDocumentTitleHandler } from '@components/utils/CustomTitleHandler';
import { getI18nProvider } from "@utils/i18nProvider";
import { useNotificationProvider } from "@utils/notificationProvider";
import { SentryPerformance } from "@components/utils/SentryPerformance";
import { SentryUserTracker } from "@components/utils/SentryUserTracker";
// import { SentryTest } from "@components/utils/SentryTest";
import { SentryErrorBoundary } from "@components/utils/SentryErrorBoundary";

// PAGES
import { UserList, UserEdit } from "@pages/users";
import { Onboarding } from "@pages/onboarding";
import { Page404 } from "@pages/404";
import { Login } from "@pages/login";
import { Home } from "@pages/home/index";
import { Config } from "@pages/config";
import { CalendarList } from "@pages/calendar";
import { Helps } from "@pages/helps";
import { GroupEvents } from "@pages/calendar/groupEvents";
import { ContentList } from "@pages/content";
import { ContentShow } from "@pages/content/show";
import { ContactProfessional } from "@pages/helps/contactProfessional";
import { TestPage } from "@pages/test";
import { CalendarCreate } from "@pages/calendar/create";
import { PQRs } from "@pages/config/pqrs";
import { PageUnauthorized } from "@pages/unauthorized";
import { AppointProfessional } from "@pages/appointment";
import { SuccessfulPayment } from "@pages/sucessful_payment";

const MainApp: React.FC = () => {
  const ref = useRef();
  const roleValue = useAtomValue(roleAtom)
  const [notificationProvider, Notification] = useNotificationProvider(ref);
  const i18nProvider = getI18nProvider();
  const [gapi, loading] = useGoogleApi()
  const gapiDataProvider = gapiDataProviderFunc(gapi)
  // if (loading) return <></> //TODO: find better way to do this
  // logger.debug("gapi", loading, gapi)

  return (
    (<BrowserRouter>
      <div className="w-full h-screen safe-areas relative font-sans scrollbar-thumb-secondary scrollbar-track-base-100">
        <DevtoolsProvider>
          <Refine
            key={roleValue.role}
            routerProvider={routerProvider}
            // liveProvider={liveProvider}
            options={{
              liveMode: "auto",
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              projectId: "2oo764-puO51k-IEDB4K"
            }}
            dataProvider={{
              default: hasuraDataProvider,
              gapi: gapiDataProvider
            }}
            notificationProvider={notificationProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            accessControlProvider={accessControlProvider}
            // ## Refine supports GraphQL subscriptions as out-of-the-box. For more detailed information, please visit here, https://refine.dev/docs/core/providers/live-provider/
            //liveProvider={liveProvider(gqlWebSocketClient)}
            //options={{ liveMode: "auto" }}
            resources={resources}
          >
            <SentryErrorBoundary>
              <SentryPerformance>
                <Notification />
                <Routes>
                  {/* Authenticated routes (generate access tokens before api calls) */}
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-routes"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <CanAccess fallback={<AppTemplate>
                          <PageUnauthorized />
                        </AppTemplate>}
                        >
                          <AppTemplate>
                            <Outlet />
                          </AppTemplate>
                        </CanAccess>
                      </Authenticated>
                    }
                  >
                    <Route
                      index
                      element={<NavigateToResource resource="home" />}
                    />
                    <Route path="/home">
                      <Route index element={<Home />} />
                    </Route>

                    <Route path="/config">
                      <Route index element={<Config />} />
                      <Route path="/config/pqrs" element={<PQRs />} />
                    </Route>

                    <Route path="/users">
                      <Route index element={<UserList />} />
                      <Route path="create" element={<UserEdit />} />
                      <Route path="edit/:id" element={<UserEdit />} />
                      {/* <Route path="show/:id" element={<PostShow />} /> */}
                    </Route>

                    <Route path="/calendar">
                      <Route index element={<CalendarList />} />
                      <Route path="/calendar/new" element={<CalendarCreate />} />
                      <Route path="/calendar/group-events" element={<GroupEvents />} />
                    </Route>

                    <Route path="/appointments/new">
                      <Route index element={<AppointProfessional />} />
                    </Route>

                    <Route path="/content">
                      <Route index element={<ContentList />} />
                      <Route path=":id" element={<ContentShow />} />
                      {/* <Route path="/calendar/group-events" element={<GroupEvents />} /> */}
                    </Route>

                    <Route path="/helps">
                      <Route index element={<Helps />} />
                    </Route>

                    <Route path="/contact-professional">
                      <Route index element={<ContactProfessional />} />
                    </Route>
                  </Route>

                  {/* Home redirection (if authenticated), if not, show login page */}
                  <Route
                    element={
                      <Authenticated key="auth-pages" fallback={<Outlet />}>
                        <NavigateToResource resource="home" />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<Login />} />
                  </Route>

                  {/*
               * Unauthenticated routes 
               */}
                  <Route
                    element={
                      <CanAccess fallback={<AppTemplate>
                        <PageUnauthorized />
                      </AppTemplate>}
                        params={{ domainRoleEnable: true }} action="list">
                        <AppTemplate><Outlet /></AppTemplate>
                      </CanAccess>
                    }
                  >
                    <Route path="/onboarding">
                      <Route index element={<Onboarding />} />
                    </Route>
                    <Route path="/successful_payment">
                      <Route index element={<SuccessfulPayment />} />
                    </Route>
                  </Route>

                  <Route path="/test">
                    <Route index element={<TestPage />} />
                  </Route>



                  {/* Catch all Unauthenticated routes */}
                  <Route
                    element={
                      <Outlet />
                    }
                  >
                    <Route path="*" element={<Page404 />} />
                  </Route>

                </Routes>
                <UnsavedChangesNotifier />
                <CustomDocumentTitleHandler />
                <DevtoolsPanel />
                <RefreshTokens />
                <SentryUserTracker />
                {/* For test errors: */}
                {/* <SentryTest /> */}
              </SentryPerformance>
            </SentryErrorBoundary>
          </Refine>
        </DevtoolsProvider>
      </div>
    </BrowserRouter >)
  );
};

export default MainApp;
